import React, { useState, useRef, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import isEmpty from 'lodash/isEmpty';
import { Avatar, SearchInput } from '@saleshandy/design-system';
import {
  ChevronDown,
  ChevronUp,
  DotsVertical,
  Pencil,
  Trash,
} from '@saleshandy/icons';
import type { IProps } from './view-list-container';
import { Pined } from '../../../../../../../../shared/svg/pined';
import { Pin } from '../../../../../../../../shared/svg/pin';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import { PinUnpinActionType } from '../../../../../../types';
import { getViewList } from '../../utils/helper';
import DeleteAlertModal from '../../../../../../../../shared/components/delete-alert-modal';
import {
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../../../../../shared/utils';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import toaster, { Theme } from '../../../../../../../../shared/toaster';
import ProspectFiltersSaveAsAViewModal from '../../../../../prospect-filters-modal/components/prospect-filters-save-as-a-view-modal';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';

const ViewList: React.FC<IProps> = ({
  pinnedFilters,
  selectedViewId,
  handlePinUnpin,
  onFilterApplied,
  onSelectAllProspects,
  userId,

  filters,
  setProspectFilters,

  teamsList,
  getTeamsListRequestStatus,
  sendGetTeamsListRequest,

  savedFilters,
  pinUnpinSavedFilterRequestStatus,
  resetPinUnpinSavedFilterRequest,
  updateSavedFilterRequestStatus,
  updateSavedFilterRequestMessage,
  deleteSavedViewRequestStatus,
  deleteSavedViewRequestMessage,
  sendUpdateSavedFilterRequest,
  sendDeleteSavedViewRequest,
  resetUpdateSavedFilterRequest,
  resetDeleteSavedViewRequest,
  getFilterViewListRequest,
}) => {
  const popupRef = useRef(null);

  // * State */
  const [search, setSearch] = useState<string>('');
  const [currentView, setCurrentView] = useState<{
    viewId: number;
    name: string;
    isShared: boolean;
    teams: any[];
  }>(null);
  const [isListVisible, setIsListVisible] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(
    false,
  );
  const [listViewToggle, setListViewToggle] = useState<{
    public: boolean;
    private: boolean;
  }>({
    public: true,
    private: true,
  });

  //* Global Variable */
  const viewList = React.useMemo(() => getViewList(savedFilters, search), [
    savedFilters,
    search,
  ]);

  const publicViews = viewList.public?.filter(
    (item) => item.filterName !== 'All Prospects',
  ).length;

  const privateViews = viewList.private?.filter(
    (item) => item.filterName !== 'All Prospects',
  ).length;

  //* Helper Functions */
  const getBtnLabel = () => {
    // Exclude the default "All Prospects" entry from the counts
    const totalSavedViews =
      savedFilters?.filter((item) => item.filterName !== 'All Prospects')
        ?.length || 0;

    const visibleSavedViews = pinnedFilters || 0;

    if (visibleSavedViews === 0 || visibleSavedViews >= totalSavedViews) {
      return 'View List';
    }

    return `+${totalSavedViews - visibleSavedViews} more`;
  };

  const showEditModal = () => {
    setIsEditModalVisible(true);
  };
  const hideEditModal = () => {
    setIsEditModalVisible(false);
  };

  const onSearch = (s) => {
    setSearch(s);
  };

  const handleUpdateView = (
    filterName: string,
    isShared: boolean,
    teamIds: number[],
  ) => {
    sendUpdateSavedFilterRequest(currentView?.viewId, {
      filterName,
      isShared,
      teamIds,
    });
  };
  const handleDeleteSavedView = () => {
    sendDeleteSavedViewRequest(currentView?.viewId);
  };

  const handleToggle = (view) => {
    setListViewToggle({
      ...listViewToggle,
      [view]: !listViewToggle[view],
    });
  };

  const handleOutsideClick = (event) => {
    // Check if click is on the button that opens/closes the popup
    const buttonElement = (event.target as HTMLElement)?.closest('.view-list__btn');
    if (buttonElement) {
      // Click is on the button, don't close the popup
      return;
    }
    
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      // Click is outside the popup, close it
      setIsListVisible(false);
    }
  };

  useEffect(() => {
    if (isListVisible) {
      // Add event listener when the popup is open
      // Use 'click' instead of 'mousedown' to ensure button's onClick fires first
      document.addEventListener('click', handleOutsideClick);
    } else {
      // Clean up the event listener when the popup is closed
      document.removeEventListener('click', handleOutsideClick);
    }

    return () => {
      // Clean up in case the component unmounts
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isListVisible]);

  useEffect(() => {
    if (
      updateSavedFilterRequestStatus === RequestStatus.Succeeded ||
      deleteSavedViewRequestStatus === RequestStatus.Succeeded
    ) {
      setIsEditModalVisible(false);
      setIsDeleteModalVisible(false);

      setProspectFilters({
        ...filters,
        shouldFetchSavedFilters: true,
      });
    }
  }, [updateSavedFilterRequestStatus, deleteSavedViewRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: pinUnpinSavedFilterRequestStatus,
      onSuccess: () => {
        resetPinUnpinSavedFilterRequest();
      },
    });
  }, [pinUnpinSavedFilterRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateSavedFilterRequestStatus,
      onSuccess: () => {
        getFilterViewListRequest();
        toaster.success(updateSavedFilterRequestMessage, { theme: Theme.New });
        resetUpdateSavedFilterRequest();
      },
    });
  }, [updateSavedFilterRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: deleteSavedViewRequestStatus,
      onSuccess: () => {
        if (currentView?.viewId && selectedViewId === currentView?.viewId) {
          onSelectAllProspects?.();
        }
        getFilterViewListRequest();
        toaster.success(deleteSavedViewRequestMessage, { theme: Theme.New });
        resetDeleteSavedViewRequest();
      },
    });
  }, [deleteSavedViewRequestStatus]);

  const renderAction = (filter) => (
    <Dropdown bsPrefix="dropdown-custom" drop="down">
      <Dropdown.Toggle
        bsPrefix="dropdown-toggle-custom"
        variant="dropdown-custom"
        id="dropdown-basic"
      >
        <DotsVertical width={14} height={12} className="gray-txt-15" />
      </Dropdown.Toggle>
      <Dropdown.Menu bsPrefix="dropdown-menu-custom">
        <Dropdown.Item
          onClick={() => {
            setCurrentView({
              viewId: filter.id,
              name: filter.filterName,
              isShared: filter.isShared,
              teams: filter.teams,
            });
            showEditModal();
          }}
          bsPrefix="dropdown-item-custom"
        >
          <Pencil width={16} height={16} className="gray-txt-15" />
          <span className="regular-2 blue-txt-16">Edit</span>
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => {
            setCurrentView({
              viewId: filter.id,
              name: filter.filterName,
              isShared: filter.isShared,
              teams: [],
            });
            setIsDeleteModalVisible(true);
          }}
          bsPrefix="dropdown-item-custom"
        >
          <Trash width={16} height={16} className="gray-txt-15" />
          <span className="regular-2 blue-txt-16">Delete</span>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
  const renderList = (label, className = '') => {
    const list =
      label === 'Public'
        ? viewList.public?.filter((item) => item.filterName !== 'All Prospects')
        : viewList.private?.filter(
            (item) => item.filterName !== 'All Prospects',
          );

    return (
      <div className={`view-list__popup-items ${className}`}>
        <div
          className="view-list__popup-item-header d-flex justify-content-between align-items-center align-items-center"
          {...accessibleOnClick(() => handleToggle(label.toLowerCase()))}
        >
          <span className="regular-2 font-medium line-height-20 popover-arrow-color-txt">
            {label}
          </span>
          {listViewToggle[label.toLowerCase()] ? (
            <ChevronUp width={18} height={18} className="gray-txt-11" />
          ) : (
            <ChevronDown width={18} height={18} className="gray-txt-11" />
          )}
        </div>
        {listViewToggle[label.toLowerCase()] && (
          <div className="view-list__popup-items-list">
            {list.map((item) => (
              <div
                key={item?.id}
                className={`view-list__popup-item ${
                  selectedViewId === item.id && 'view-list__popup-item-active'
                } d-flex justify-content-between align-items-center gap-8`}
              >
                <div
                  className="d-flex align-items-center"
                  style={{ width: '80%' }}
                  {...accessibleOnClick(() => {
                    onFilterApplied(item);
                    setIsListVisible(false);
                  })}
                >
                  <OverlayTooltip
                    text={isEmpty(item.pinnedAt) ? 'Pin View' : 'Unpin View'}
                    rootClose={true}
                    rootCloseEvent="click"
                  >
                    <div
                      className="pin-unpin-btn"
                      {...accessibleOnClick((e) => {
                        if (
                          getIsRequestPending(pinUnpinSavedFilterRequestStatus)
                        ) {
                          return;
                        }

                        e.stopPropagation();

                        handlePinUnpin(
                          item.id,
                          isEmpty(item.pinnedAt)
                            ? PinUnpinActionType.Pin
                            : PinUnpinActionType.UnPin,
                        );
                      })}
                    >
                      {isEmpty(item.pinnedAt) ? (
                        <Pin className="gray-txt-15" />
                      ) : (
                        <Pined />
                      )}
                    </div>
                  </OverlayTooltip>
                  <TruncatedTextWithAutoTooltip
                    text={item?.filterName}
                    className="regular-2 popover-arrow-color-txt line-height-20 ml-2"
                  />
                </div>
                <div className="view-list__popup-item-action align-items-center">
                  <OverlayTooltip
                    text={`${item?.created?.firstName} ${item?.created?.lastName}`}
                  >
                    <Avatar
                      size="sm"
                      firstName={item?.created?.firstName}
                      lastName={item?.created?.lastName}
                      className="mr-2"
                    />
                  </OverlayTooltip>
                  {userId === item?.created.id && renderAction(item)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="view-list">
        <div
          className="view-list__btn position-relative d-flex align-items-center"
          {...accessibleOnClick((e) => {
            e.stopPropagation();
            setIsListVisible(!isListVisible);
          })}
        >
          {getBtnLabel()}
          <ChevronDown width={16} height={16} className="ml-1" />
        </div>

        {isListVisible && (
          <div className="view-list__popup" ref={popupRef}>
            <div className="view-list__popup-input">
              <SearchInput
                search={search}
                value={search}
                onSearch={() => {}}
                onClearSearch={() => {}}
                setSearch={(e) => onSearch(e)}
              />
            </div>

            {publicViews > 0 && renderList('Public', 'show-border-bottom')}
            {privateViews > 0 && renderList('Private')}
          </div>
        )}
      </div>

      {isEditModalVisible && (
        <ProspectFiltersSaveAsAViewModal
          show={isEditModalVisible}
          viewDetails={currentView}
          onHide={hideEditModal}
          onSave={handleUpdateView}
          teamsList={teamsList}
          getTeamsListRequestStatus={getTeamsListRequestStatus}
          sendGetTeamsListRequest={sendGetTeamsListRequest}
          isLoading={getIsRequestPending(updateSavedFilterRequestStatus)}
        />
      )}

      {isDeleteModalVisible && (
        <DeleteAlertModal
          show={isDeleteModalVisible}
          className="delete-saved-view"
          onClose={() => setIsDeleteModalVisible(false)}
          onSubmit={() => handleDeleteSavedView()}
          title="Delete Saved View"
          contents={[
            <span>
              Are you sure you want to delete “
              <span className="break-word">{currentView?.name}</span>” saved
              view?
            </span>,
          ]}
          isSubmitLoading={getIsRequestPending(deleteSavedViewRequestStatus)}
          submitButtonText="Yes"
        />
      )}
    </>
  );
};

export default ViewList;
